import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
    IDataPropertiesOnlyRequired,
    IReportPageProperties,
    IReportData,
    IAggregation,
    ISelectedColumns,
    IQuickReportExport,
    IRowItem,
    IExportColumnInfo,
    IRow,
    ITableColumns,
} from '../interfaces/reporting-data';
import { saveAs } from 'file-saver';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { CurrentUserStateService } from 'app/modules/shared/services/current-user-state.service';
import { ImpersonationService } from 'app/modules/shared/services/impersonation.service';
import { ICustomTableInfoExtend } from 'app/modules/shared/components/custom-mat-table/custom-mat-table.component';
import { NoDataActionType } from 'app/modules/shared/interfaces/no-data';

@Injectable({
    providedIn: 'root',
})
export class ReportingService {
    constructor(
        private _http: HttpClient,
        private _reusableFunctions: ReusableFunctions,
        private _currentUserStateService: CurrentUserStateService,
        private _impersonationService: ImpersonationService
    ) {}

    private serverUrl = 'https://' + window.location.host;
    private version = '0.5';
    private _url = (this.serverUrl + '/api/v{version}/reporting').replace(
        '{version}',
        this.version
    );

    getReportData(properties: IDataPropertiesOnlyRequired): Observable<IReportData> {
        let body = {
            parameters: JSON.stringify(properties),
        };
        return this._http.post<IReportData>(this._url + '/GetReportData/', body);
    }

    getApiGatewayUrl(): Observable<string> {
        return this._http.get<string>(this._url + '/GetApiGatewayUrl', {});
    }

    getApiGatewayUrlByApiInfo(apiName: string, stage: string): Observable<string> {
        const params = new HttpParams().set('apiName', apiName).set('stage', stage);
        return this._http.get<string>(this._url + '/GetApiGatewayUrlByApiInfo', {
            params: params,
        });
    }

    getInvoiceData(startDt: Date, endDt: Date) {
        const body: any = {
            startDt: startDt,
            endDt: endDt,
        };

        return this._http.post<string>(this._url + '/GetInvoicedLoans', body);
    }

    setHeaders() {
        const headers = new HttpHeaders();
        headers.set('Access-Control-Allow-Origin', '*');
        headers.set(
            'Access-Control-Allow-Headers',
            'Sec-Ch-Ua-Platform,User-Agent,Sec-Ch-Ua-Mobile,Sec-Ch-Ua,Referer,cache-control,Content-Type,X-Amz-Date,Authorization,X-Api-Key,X-Amz-Security-Token,LG-Impersonate,Origin,X-Requested-With,Accept,Access-Control-Request-Method'
        );
        return headers;
    }

    getReportDataDirectApi(
        properties: IDataPropertiesOnlyRequired,
        baseUrl: string,
        headers: HttpHeaders
    ): Observable<any> {
        let body = {
            parameters: JSON.stringify(properties),
            clientShortName: properties.clientShortName,
            loginEmail: properties.loginEmail,
            bucketId: properties.bucketId,
            isFirstDataRequest: properties.isFirstDataRequest,
            outType: '0',
        };

        let url = baseUrl + '/direct-reporting/get-report-data';
        return this._http.post<any>(url, body, { headers: headers });
    }

    removeCachedData(
        properties: IDataPropertiesOnlyRequired,
        baseUrl: string,
        headers: HttpHeaders
    ) {
        let body = {
            parameters: JSON.stringify(properties),
            clientShortName: properties.clientShortName,
            loginEmail: properties.loginEmail,
            bucketId: properties.bucketId,
            isFirstDataRequest: '-1',
            outType: '0',
            hasDownloadRequested: properties.hasDownloadRequested ? '1' : '0',
        };
        let url = baseUrl + '/direct-reporting/get-report-data';
        return this._http.post<any>(url, body, { headers: headers });
    }

    getCurrentUserInfo() {
        let currentUserInfo = {
            clientShortName: '',
            currentUserName: '',
        };

        let currentUser = this._currentUserStateService.currentUser;
        currentUserInfo.currentUserName = currentUser.username;
        if (this._impersonationService.impersonateClient) {
            currentUserInfo.clientShortName = this._impersonationService.impersonateClient;
        } else {
            currentUserInfo.clientShortName = currentUser.clientShortName;
        }
        return currentUserInfo;
    }

    getDataProperties(
        moduleName: string,
        reportId: string,
        ownerPosition: number,
        staffingKey: number
    ): Observable<IReportPageProperties> {
        const params = new HttpParams()
            .set('moduleName', moduleName)
            .set('ownerStaffing', ownerPosition ? ownerPosition.toString() : '')
            .set('staffingKey', staffingKey ? staffingKey.toString() : '')
            .set('reportId', reportId);
        return this._http.get<IReportPageProperties>(this._url + '/GetDataProperties', {
            params: params,
        });
    }

    getAggregationData(properties: IDataPropertiesOnlyRequired): Observable<IAggregation[]> {
        let body = {
            parameters: JSON.stringify(properties),
        };
        return this._http.post<IAggregation[]>(this._url + '/GetAggregationData/', body);
    }

    getAggregationDataFromDirectApi(
        properties: IDataPropertiesOnlyRequired,
        baseApiUrl: string,
        headers: HttpHeaders
    ): Observable<IAggregation[]> {
        let body = {
            parameters: JSON.stringify(properties),
            isFirstDataRequest: '0',
            bucketId: properties.bucketId,
            clientShortName: properties.clientShortName,
            loginEmail: properties.loginEmail,
            outType: '1',
        };
        let url = baseApiUrl + '/direct-reporting/get-report-data';
        return this._http.post<IAggregation[]>(url, body, { headers: headers });
    }

    reportExportTo(
        properties: IDataPropertiesOnlyRequired,
        baseApiUrl: string,
        headers: HttpHeaders,
        type: 'pdf' | 'excel' | 'csv' = 'excel'
    ): Observable<any> {
        let body = {
            parameters: JSON.stringify(properties),
            isFirstDataRequest: '0',
            bucketId: properties.bucketId,
            clientShortName: properties.clientShortName,
            loginEmail: properties.loginEmail,
            outType: '2',
        };
        let url = baseApiUrl + '/direct-reporting/get-report-data';
        let contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        headers.set('Content-Type', contentType);
        //headers.set('responseType', 'arraybuffer');
        return this._http.post<any>(url, body, {
            headers: headers,
            responseType: 'arraybuffer' as 'json', //'blob' as 'json',
        });

        //
        //
        // return this._http.post<Blob>(this._url + '/ReportExportTo/', body, {
        //     responseType: 'blob' as 'json',
        // });
    }

    getTestReportExport(
        properties: IDataPropertiesOnlyRequired,
        type: 'pdf' | 'excel' | 'csv'
    ): Observable<Blob> {
        let body = {
            parameters: JSON.stringify(properties),
            type: type,
        };
        return this._http.post<Blob>(this._url + '/TestReportExportTo/', body, {
            responseType: 'blob' as 'json',
        });
    }

    reportExportToPdf(
        properties: IDataPropertiesOnlyRequired,
        type: 'pdf' | 'excel' | 'csv'
    ): Observable<number> {
        let body = {
            parameters: JSON.stringify(properties),
            type: type,
        };
        return this._http.post<number>(this._url + '/ReportExportToPdf/', body, {});
    }

    quickReportExportTo(
        properties: IQuickReportExport,
        type: 'pdf' | 'excel' | 'csv'
    ): Observable<Blob> {
        const body = {
            parameters: JSON.stringify(properties),
            type: type,
        };
        return this._http.post<Blob>(this._url + '/QuickReportExportTo/', body, {
            responseType: 'blob' as 'json',
        });
    }

    updateSelectedColumns(properties: ISelectedColumns[]): Observable<number> {
        let body = {
            parameters: JSON.stringify(properties),
        };
        return this._http.post<number>(this._url + '/UpdateSelectedColumns/', body, {});
    }

    setQuickExportData(
        columnInfo: IExportColumnInfo[],
        exportData: Array<any>,
        exportName: string,
        hasColumnsMapped = false,
        processedRows: IRow[] = []
    ) {
        let exportRequest: IQuickReportExport = {
            reportDataSource: exportName,
            columns: [],
            rows: [],
        };

        if (hasColumnsMapped) {
            exportRequest.columns = columnInfo;
            exportRequest.rows = processedRows;
        } else {
            columnInfo.forEach((col, index) => {
                exportRequest.columns.push({
                    columnId: index + 1,
                    columnName: col.columnName,
                });
            });

            if (exportData && exportData.length > 0) {
                let keys = Object.keys(exportData[0]);

                exportData.forEach((dataLine, rowId) => {
                    let rowItems: Array<IRowItem> = [];
                    columnInfo.forEach((colInf, idx) => {
                        let datapoint = keys.find((key) => colInf.columnIdName === key);
                        let rowItm: IRowItem = {
                            columnId: idx,
                            dataType: colInf.dataType,
                            value: '',
                            isNegativeValueBad: false,
                        };

                        if (datapoint) {
                            rowItm.value = dataLine[colInf.columnIdName];
                            rowItm.isNegativeValueBad = dataLine['isNegativeValueBad'] ?? false;
                        }
                        rowItems.push(rowItm);
                    });

                    exportRequest.rows.push({
                        rowId: rowId + 1,
                        rowData: rowItems,
                        ////rowOutlineLevel: dataLine['rowOutlineLevel'] ?? 0,  ////
                    });
                });
            }
        }

        if (exportRequest.columns.length > 0) {
            let dateTime = this._reusableFunctions.getCurrentDateTimeStamp();
            let reportName = exportRequest.reportDataSource + '_' + dateTime + '_.xlsx';
            this.quickReportExportTo(exportRequest, 'excel').subscribe((blob) => {
                if (blob) {
                    saveAs(blob, reportName);
                }
            });
        }
    }

    setExportColumnsAndExportToExcel(
        columnInfoList: ITableColumns[],
        dataset: Array<any>,
        exportName,
        type = 'excel'
    ) {
        // TODO: make this method to use with csv as well

        if (columnInfoList?.length > 0) {
            let columnInfoArray: IExportColumnInfo[] = [];
            columnInfoList.forEach((column, index) => {
                if (!(column.hideIf === 'excel')) {
                    columnInfoArray.push({
                        columnId: index,
                        columnName: column.fieldName,
                        columnIdName: column.fieldId,
                        precision: column.decimalPlaces ?? 0,
                        dataType: this.mapToExcelDataType(column.dataType),
                    });
                }
            });

            this.setQuickExportData(columnInfoArray, dataset, exportName, false);
        } else {
            console.error('No columns found for export');
            throw new Error('Unable to export - no columns specified');
        }
    }

    mapToExcelDataType(dataType: any) {
        switch (dataType) {
            //TODO: Add logic to convert fot boolean and percentage
            case 'number':
                return 'Numeric';
            case 'date':
                return 'Date';
            case 'dateTime':
                return 'DateTime';
            case 'money':
                return 'Currency';
            case 'currency':
                return 'Currency';
            default:
                return 'String';
        }
    }

    refreshCustomMatTable(
        customTableInfo: ICustomTableInfoExtend,
        hasData: boolean,
        isDataReady: boolean,
        noDataType: NoDataActionType,
        searchTerm: string = ''
    ) {
        let customTableInfoCopy = customTableInfo;
        customTableInfoCopy.hasData = hasData;
        customTableInfoCopy.isDataReady = isDataReady;
        customTableInfoCopy.action = noDataType;
        customTableInfoCopy.searchTerm = searchTerm;
        return Object.assign({}, customTableInfoCopy);
    }
}
